import {i18n} from "../../i18n.ts"
import {
  AKlaraDashboardUpdateCenterEntry,
  AKlaraDashboardUpdateCenterEvent,
  ASalesAssistantUpdateCenterEntryTypeValues,
  ASalesAssistantUpdateCenterStepTypeValues,
} from "../../services/types.generated.ts"
import {formatDate} from "./components/formatDate.ts"
import {leadName} from "./leadName.ts"

/** A session of the dashboard update feed. */
export type TKlaraFeedEntry = AKlaraDashboardUpdateCenterEntry

export type TKlaraSessionSummary = {
  dealsCount: number
  activitiesCount: number
  hasWon: boolean
  stageMoves: number
  /** Distinct organisation labels in feed order (the lead name fallback for a deal without a named organisation). */
  organisations: string[]
  isCrm: boolean
}

export type TKlaraSessionDay = {
  date: string
  entries: TKlaraFeedEntry[]
}

/** Counts derived client-side from one feed entry (a session): one event per deal, one step per activity. */
export const summarizeEntry = (entry: TKlaraFeedEntry): TKlaraSessionSummary => {
  const events = entry.update_center_events
  const steps = events.flatMap(event => event.update_center_steps)

  return {
    dealsCount: events.length,
    activitiesCount: steps.length,
    hasWon: steps.some(step => step.step_type === ASalesAssistantUpdateCenterStepTypeValues.DealWon),
    stageMoves: steps.filter(step => step.step_type === ASalesAssistantUpdateCenterStepTypeValues.Stage).length,
    organisations: organisationLabels(events),
    isCrm: entry.entry_type === ASalesAssistantUpdateCenterEntryTypeValues.CrmSystem,
  }
}

/**
 * Distinct labels in feed order: deals sharing an organisation or title share a label, while each deal without
 * either keeps its own placeholder entry.
 */
const organisationLabels = (events: readonly AKlaraDashboardUpdateCenterEvent[]) => {
  const labels = new Map<string, string>()
  for (const {deal} of events) {
    const known = deal.organization?.name?.trim() || deal.title?.trim()
    labels.set(known ? `name:${known}` : `deal:${deal.id}`, known || feedDealName(deal))
  }
  return Array.from(labels.values())
}

/** The session's sales person as initials, "?" without a name. */
export const entryInitials = (entry: TKlaraFeedEntry) =>
  [entry.sales_person?.first_name, entry.sales_person?.last_name]
    .map(part => part?.trim().charAt(0))
    .filter(Boolean)
    .join("")
    .toUpperCase() || "?"

/** The feed's deal carries no contact, so the lead name falls back to the organisation, then the title. */
export const feedDealName = (deal: AKlaraDashboardUpdateCenterEvent["deal"]) => leadName({...deal, contact: null})

/** Groups feed entries by `tracked_date`, keeping the feed order of days and of entries within a day. */
export const groupEntriesByDay = (entries: readonly TKlaraFeedEntry[]): TKlaraSessionDay[] => {
  const days = new Map<string, TKlaraFeedEntry[]>()

  for (const entry of entries) {
    const day = days.get(entry.tracked_date)
    if (day) {
      day.push(entry)
    } else {
      days.set(entry.tracked_date, [entry])
    }
  }

  return Array.from(days, ([date, dayEntries]) => ({date, entries: dayEntries}))
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Wall-clock date and time of an instant in `timeZone` (the browser's zone when omitted). */
export const wallClock = (date: Date, timeZone?: string) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)?.value ?? ""

  return {
    year: Number(part("year")),
    month: Number(part("month")),
    day: Number(part("day")),
    time: `${part("hour")}:${part("minute")}`,
  }
}

const dayNumber = ({year, month, day}: {year: number; month: number; day: number}) =>
  Date.UTC(year, month - 1, day) / MS_PER_DAY

/** "14:38" for a session's `tracked_at`, read in `timeZone` (the browser's zone when omitted). */
export const formatSessionClock = (trackedAt: string, timeZone?: string): string =>
  wallClock(new Date(trackedAt), timeZone).time

/**
 * "Today 14:38", "Yesterday 09:12" or "25 Sep, 09:12" for a session's `tracked_at`, with both the session and
 * `now` read on the calendar of `timeZone` (the browser's zone when omitted).
 */
export const formatSessionTime = (trackedAt: string, {now, timeZone}: {now: Date; timeZone?: string}): string => {
  const tracked = wallClock(new Date(trackedAt), timeZone)
  const daysAgo = dayNumber(wallClock(now, timeZone)) - dayNumber(tracked)

  if (daysAgo === 0) {
    return i18n.t("KlaraDashboard_SessionTime_Today", {time: tracked.time})
  }
  if (daysAgo === 1) {
    return i18n.t("KlaraDashboard_SessionTime_Yesterday", {time: tracked.time})
  }

  // Noon keeps the formatted day clear of the browser's own DST transitions.
  const day = formatDate(new Date(tracked.year, tracked.month - 1, tracked.day, 12).getTime(), "d MMM")
  return i18n.t("KlaraDashboard_SessionTime_Date", {date: day, time: tracked.time})
}
